import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Offcanvas, Form, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';
import { useUserContext } from 'providers/UserProvider';

import { picklistApi } from 'api';
import paths from 'routes/paths';
import { REJECTED_SHIPMENT_LINE_STATUSES } from 'constants/shipment';

const ShipmentHeader = ({ shipmentData }) => {
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  return (
    <div className="bg-light p-3 d-flex align-items-center">
      <div className="d-flex flex-grow-1">
        <div className="fs-10 flex-1">
          <strong>Shipment Code</strong>
          <div>
            <Link
              to={`${paths.orders.details(
                tenant,
                warehouse,
                shipmentData.order?.id,
              )}?tab=shipments`}
            >
              <strong>{shipmentData.shipment_code}</strong>
            </Link>
          </div>
        </div>

        <div className="fs-10 flex-1">
          <strong>Shipment Status</strong>
          <div>
            <SubtleBadge bg="secondary">
              {shipmentData.shipment_status_label}
            </SubtleBadge>
          </div>
        </div>

        <div className="fs-10 flex-1">
          <strong>Order</strong>
          <div>
            <Link
              to={`${paths.orders.details(
                tenant,
                warehouse,
                shipmentData.order?.id,
              )}?tab=order-items`}
            >
              <strong>{shipmentData.order?.id}</strong>
            </Link>
          </div>
        </div>

        <div className="fs-10 flex-1">
          <strong>Channel</strong>
          <div>{shipmentData.order?.channel?.channel_type}</div>
        </div>
      </div>
    </div>
  );
};

const ShipmentLineTableRow = React.memo(
  ({ line, shipmentLineValues, onUpdateLine }) => {
    const isRejected = REJECTED_SHIPMENT_LINE_STATUSES.includes(
      line.shipment_line_status,
    );

    const handleStatusChange = e => {
      const newStatus = e.target.value;

      onUpdateLine(line.id, {
        ...shipmentLineValues,
        status: newStatus,
        rejection_reason: newStatus === 'reject_damaged' ? '' : '',
      });
    };

    const handleReasonChange = e => {
      onUpdateLine(line.id, {
        ...shipmentLineValues,
        rejection_reason: e.target.value,
      });
    };

    return (
      <tr className="align-middle white-space-nowrap">
        <td className="align-middle">{line.product?.name || '-'}</td>
        <td className="align-middle">{line.product?.sku || '-'}</td>
        <td className="align-middle text-center">
          <SubtleBadge bg={isRejected ? 'danger' : 'secondary'}>
            {line.shipment_line_status_label}
          </SubtleBadge>
        </td>
        <td className="align-middle">
          <Form.Select
            size="sm"
            value={shipmentLineValues.status || ''}
            onChange={handleStatusChange}
            disabled={isRejected}
            style={{ width: '200px' }}
          >
            <option value="">Select Action</option>
            <option value="reject_not_found">Not Found</option>
            <option value="reject_damaged">Damaged</option>
          </Form.Select>
        </td>
        <td className="align-middle" style={{ width: '30%' }}>
          {shipmentLineValues.status === 'reject_damaged' &&
            (isRejected ? (
              shipmentLineValues.rejection_reason || '-'
            ) : (
              <Form.Control
                size="sm"
                placeholder="Enter Rejection Reason"
                value={shipmentLineValues.rejection_reason || ''}
                onChange={handleReasonChange}
                required
                isInvalid={!shipmentLineValues.rejection_reason}
              />
            ))}
        </td>
      </tr>
    );
  },
);

const PickedShipmentLineRejectPanel = ({
  show,
  onHide,
  picklistId,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [shipmentData, setShipmentData] = useState(null);
  const [shipmentLineRowData, setShipmentLineRowData] = useState({});

  useEffect(() => {
    if (show) {
      const fetchData = async () => {
        try {
          setLoading(true);
          const response = await picklistApi.getPickedShipment(picklistId);
          setShipmentData(response);

          const initialShipmentLineRowData = {};
          response.lines.forEach(line => {
            const isRejected = REJECTED_SHIPMENT_LINE_STATUSES.includes(
              line.shipment_line_status,
            );

            initialShipmentLineRowData[line.id] = {
              status: isRejected ? line.shipment_line_status : '',
              rejection_reason: line?.rejection_reason || '',
            };
          });
          setShipmentLineRowData(initialShipmentLineRowData);
        } catch (error) {
          toast.error('Failed to load shipment data');
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [show, picklistId]);

  const handleUpdateLine = (lineId, values) => {
    setShipmentLineRowData(prev => ({
      ...prev,
      [lineId]: values,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // request body 에 원래 reject 인 요소들 제외
      const newRejectedLines = Object.entries(shipmentLineRowData)
        .filter(([id, data]) => {
          const originalLine = shipmentData.lines.find(
            line => line.id === Number(id),
          );

          // table 에서 rejected 상태로 되었는지 체크
          const isMarkedAsRejectedInTable =
            REJECTED_SHIPMENT_LINE_STATUSES.includes(data.status);

          const wasNotAlreadyRejected =
            !REJECTED_SHIPMENT_LINE_STATUSES.includes(
              originalLine.shipment_line_status,
            );

          return isMarkedAsRejectedInTable && wasNotAlreadyRejected;
        })
        .map(([id, data]) => ({
          id: Number(id),
          rejection_status: data.status,
          rejection_reason:
            data.status === 'reject_damaged' ? data.rejection_reason : '',
        }));

      if (newRejectedLines.length === 0) {
        toast.error('No items marked as rejected');
        return;
      }

      await picklistApi.setShipmentLineStatusToRejected(picklistId, {
        rejected_lines: newRejectedLines,
      });

      toast.success('Items updated successfully');
      await onRefresh();
      onHide();
    } catch (error) {
      toast.error('Failed to update items');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="w-75">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Edit Shipment</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="pb-7">
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : shipmentData ? (
          <>
            <div className="bg-light p-3 mb-3">
              <div className="mb-2">
                <SubtleBadge>INSTRUCTION</SubtleBadge>
              </div>
              <div className="fs-10">
                Mark items as 'Not Found' or 'Damaged'
                <br />
                For damaged items, rejection reason is mandatory.
              </div>
            </div>

            <ShipmentHeader shipmentData={shipmentData} />

            <div className="mt-2">
              <table className="table table-sm fs-10 mb-0 overflow-hidden">
                <thead>
                  <tr>
                    <th className="bg-light text-900 text-nowrap align-middle">
                      Name
                    </th>
                    <th className="bg-light text-900 text-nowrap align-middle">
                      SKU Code
                    </th>
                    <th className="bg-light text-900 text-nowrap align-middle text-center">
                      Item Status
                    </th>
                    <th className="bg-light text-900 text-nowrap align-middle">
                      Actions
                    </th>
                    <th
                      className="bg-light text-900 text-nowrap align-middle"
                      width="30%"
                    >
                      Rejection Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shipmentData.lines.map(line => (
                    <ShipmentLineTableRow
                      key={line.id}
                      line={line}
                      shipmentLineValues={shipmentLineRowData[line.id]}
                      onUpdateLine={handleUpdateLine}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </Offcanvas.Body>

      {shipmentData && (
        <div className="position-absolute w-100 bottom-0 p-3 bg-white border-top">
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={onHide} disabled={submitting}>
              CANCEL
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              SUBMIT
            </Button>
          </div>
        </div>
      )}
    </Offcanvas>
  );
};

export default PickedShipmentLineRejectPanel;
